'use client';

import React from 'react'
import { Partners } from '@/lib/types'
import { useIsTb } from '@/contexts/BreakpointContext';

type DevicePartnerBadgeSize = "desktop" | "mobile"

interface DevicePartnerBadgeProps {
	title?: string
	partner?: Partners
	size?: DevicePartnerBadgeSize
	disableBg?: boolean
	darkMode?: boolean
}

// Map device names (both English and Arabic) to partner types
const DEVICE_NAME_TO_PARTNER: Record<string, Partners> = {
	// English device names
	'Tesla': 'teslamed',
	'TESLA MED': 'teslamed',
	'Revive RP': 'revive',
	'Revive Radiance Pro': 'revive',
	'Torpedo': 'hitek',
	'HITEK TORPEDO': 'hitek',
	'Pressomax': 'hitek',
	'HITEK PRESSOMAX': 'hitek',
	'Face Analyzer': 'placeholder',
	'Body Analyzer': 'placeholder',
	'Body Scanner': 'placeholder',
	// Arabic device names
	'تسلا': 'teslamed',
	'تسلا-ميد': 'teslamed',
	'ريفيف رادينس برو': 'revive',
	'ريڤيڤ رادينس برو': 'revive',
	'توربيدو': 'hitek',
	'هايتك توربيدو': 'hitek',
	'بريسوماكس': 'hitek',
	'هايتك بريسوماكس': 'hitek',
	'محلل الوجه': 'placeholder',
	'ماساج الجسم': 'placeholder',
}

// Helper function to get partner from device name
const getPartnerFromDeviceName = (deviceName: string): Partners => {
	return DEVICE_NAME_TO_PARTNER[deviceName] || 'placeholder'
}

const PARAMS_TO_SVG: Record<DevicePartnerBadgeSize, Record<Partners, string>> = {

	desktop: {
		hitek: '/Partner-Hitek-Desktop.svg',
		teslamed: '/Partner-Teslamed-Desktop.svg',
		revive: '/Partner-Revive-Desktop.svg',
		placeholder: '/Partner-Placeholder-Desktop.svg'
	},
	mobile: {
		hitek: '/Partner-Hitek-Mobile.svg',
		teslamed: '/Partner-Teslamed-Mobile.svg',
		revive: '/Partner-Revive-Mobile.svg',
		placeholder: '/Partner-Placeholder-Mobile.svg'
	}
}

const PARAMS_TO_SVG_DARK: Record<DevicePartnerBadgeSize, Record<Partners, string>> = {

	desktop: {
		hitek: '/Partner-Hitek-Desktop-Dark.svg',
		teslamed: '/Partner-Teslamed-Desktop-Dark.svg',
		revive: '/Partner-Revive-Desktop-Dark.svg',
		placeholder: '/Partner-Placeholder-Desktop-Dark.svg'
	},
	mobile: {
		hitek: '/Partner-Hitek-Mobile-Dark.svg',
		teslamed: '/Partner-Teslamed-Mobile-Dark.svg',
		revive: '/Partner-Revive-Mobile-Dark.svg',
		placeholder: '/Partner-Placeholder-Mobile-Dark.svg'
	}
}

const DevicePartnerBadge: React.FC<DevicePartnerBadgeProps> = ({
	title,
	partner: partnerProp,
	size,
	disableBg = false,
	darkMode = false,
}) => {
	const isTb = useIsTb();
	const badgeSize = size || (isTb ? "desktop" : "mobile");
	// Use partner prop if provided, otherwise map from title
	const partner = partnerProp || (title ? getPartnerFromDeviceName(title) : 'placeholder');

	// Determine padding based on partner and size
	const getPadding = () => {
		if (disableBg) return '';
		if (partner === 'placeholder') {
			return badgeSize === 'mobile' 
				? 'py-[0.125rem] px-[0.625rem]'  // 2px mobile
				: 'py-[0.1875rem] px-[0.75rem]';  // 3px desktop
		}
		return badgeSize === 'mobile' 
			? 'py-[0.375rem] px-[0.625rem]'
			: 'py-[0.5rem] px-[0.75rem]';
	};

	return (

		<div className=
			{`
			${disableBg ? 'bg-transparent' : 'bg-theme-yellow-primary-base'}
			${getPadding()}

			rounded-full
			w-fit
			

			`}
		>
			<img src={darkMode ? PARAMS_TO_SVG_DARK[badgeSize][partner] : PARAMS_TO_SVG[badgeSize][partner]}
			 alt="Logo Decoration Motif" className={` ${disableBg ? 'h-[0.75rem] lg:h-[0.875rem]' : 'self-center'}`} />

		</div>
	);
}

export default DevicePartnerBadge;
